<template>
  <div
    :class="$b({ isPressed, inCart })"
    @mousedown="pressed"
    @touchstart="pressed"
    @mouseup="released"
    @touchend="released"
  >
    <div :class="$b('base-image')">
      <DatoCmsImage
        v-if="previousSelectedNakedEyeImage"
        :key="previousSelectedNakedEyeImage.src"
        :class="$b('scene-image', { previous: true })"
        :data="previousSelectedNakedEyeImage"
        :priority="true"
      />
      <DatoCmsImage
        v-if="hasScenes"
        :key="selectedNakedEyeImage.src"
        :class="$b('scene-image')"
        :data="selectedNakedEyeImage"
        :priority="true"
        @load="handleNakedEyeLoaded"
      />
      <BaseOverlay
        v-if="isMdBreakpointOrAbove"
        :gradient="true"
        gradient-height="15%"
        gradient-direction="bottom-up"
        gradient-theme="black"
      />
      <BaseCopy
        :class="$b('label', { desktop: true })"
        tag="p"
        size="h4"
      >
        {{ $tr('Naked Eye.') }}
      </BaseCopy>
    </div>
    <div
      :class="$b('preview-image')"
      :style="{ ...clip }"
    >
      <div :class="$b('preview-image-wrapper')">
        <DatoCmsImage
          v-if="previousPrimarySceneImage"
          :key="previousPrimarySceneImage.src"
          :class="$b('scene-image', { previous: true })"
          :data="previousPrimarySceneImage"
          :priority="true"
        />
        <DatoCmsImage
          v-if="hasScenes && primarySceneImage"
          :key="primarySceneImage.src"
          :class="$b('scene-image')"
          :data="primarySceneImage"
          :priority="true"
          @load="handlePrimaryLoaded"
        />
      </div>
      <FadeTransition>
        <div
          v-if="secondarySceneImage && showIrisPreview"
          :class="$b('preview-image-wrapper')"
        >
          <DatoCmsImage
            v-if="previousSecondarySceneImage"
            :key="previousSecondarySceneImage.src"
            :class="$b('scene-image', { previous: true })"
            :data="previousSecondarySceneImage"
            :priority="true"
          />
          <DatoCmsImage
            v-if="hasScenes && secondarySceneImage"
            :key="secondarySceneImage.src"
            :class="$b('scene-image', { secondary: secondarySceneImage })"
            :data="secondarySceneImage"
            :priority="true"
            @load="handleSecondaryLoaded"
          />
        </div>
      </FadeTransition>
      <BaseOverlay
        v-if="isMdBreakpointOrAbove"
        :gradient="true"
        gradient-height="15%"
        gradient-direction="bottom-up"
        gradient-theme="black"
      />
      <div
        v-if="
          (customisedProduct.lensTechSelected || !hasLensTechFiltering)
            && lensTechIcon
        "
        :class="$b('lens-tech-icon-wrapper')"
      >
        <VisionIcon
          :icon="lensTechIcon"
          :class="$b('lens-tech-icon')"
          colour="white"
          size="stretch"
        />
      </div>
    </div>
    <VisionIcon
      v-if="isMdBreakpointOrAbove"
      :icon="grabbing ? 'hand-rock-o' : 'hand-paper-o'"
      :style="{ left: `${xPercent}%` }"
      :class="$b('grab-icon')"
      colour="black"
    />
    <input
      v-if="isMdBreakpointOrAbove"
      v-model="xPercent"
      :class="$b('range-input', { grabbing })"
      :style="{ left: `${minValue}%`, right: `${minValue}%` }"
      type="range"
      :min="minValue"
      :max="maxValue"
      step="0.01"
      tabindex="-1"
      @mousedown="grab"
      @touchstart="grab"
      @mouseup="release"
      @touchend="release"
      @mouseleave="release"
    />
    <FadeTransition>
      <div
        v-if="productMode && !isMdBreakpointOrAbove"
        :class="$b('product-image')"
      >
        <StaticThreeSixtyFrame
          v-if="customisedProduct.product.show360"
          :customised-product="customisedProduct"
          :show-iris-preview="showIrisPreview"
          :frame-number="14"
          :width="740"
          :height="320"
          :class="$b('static-360-frame')"
        />
        <ProductImg
          v-else
          :view-angle="2"
          :customised-product="customisedProduct"
          :show-iris-preview="showIrisPreview"
          :class="$b('image')"
        />
      </div>
    </FadeTransition>

    <FadeTransition>
      <div
        v-if="showOverlay"
        :class="$b('overlay')"
      />
    </FadeTransition>
    <FadeTransition>
      <div
        v-if="
          (showInstruction ||
            customisedProduct.lensTechSelected ||
            !hasLensTechFiltering ||
            isPressed) &&
            !productMode &&
            !isMdBreakpointOrAbove
        "
        :class="$b('gradient-overlay')"
      >
        <BaseOverlay
          :gradient="true"
          gradient-height="30%"
          gradient-direction="bottom-up"
          gradient-theme="black"
        />
      </div>
    </FadeTransition>
    <FadeTransition>
      <div v-if="showInstruction && !productMode">
        <div :class="$b('icon-wrapper')">
          <VisionIcon
            icon="press-hold"
            size="x-large"
            colour="white"
          />
        </div>
        <div :class="$b('label-wrapper')">
          <BaseCopy
            :class="$b('label')"
            tag="p"
            size="h5"
          >
            {{ $tr(label) }}
          </BaseCopy>
        </div>
      </div>
    </FadeTransition>
    <FadeTransition>
      <div
        v-if="isPressed && !productMode"
        :class="$b('label-wrapper')"
      >
        <BaseCopy
          :class="$b('label')"
          tag="p"
          size="h5"
        >
          {{ $tr(label) }}
        </BaseCopy>
      </div>
    </FadeTransition>
    <FadeTransition>
      <VisionIcon
        v-if="
          (customisedProduct.lensTechSelected || !hasLensTechFiltering) &&
            !showInstruction &&
            !isPressed &&
            !productMode &&
            lensTechIcon
        "
        :icon="lensTechIcon"
        :class="$b('lens-icon')"
        size="stretch"
        colour="white"
      />
    </FadeTransition>
  </div>
</template>

<script>
import { getAllScenes } from '@/gql/product/lensGuideScene.gql.js'
import CustomisedProduct from '~/models/CustomisedProduct'
import { Image as DatoCmsImage } from 'vue-datocms'

export default {
  name: 'LensGuideMainImage',
  components: {
    DatoCmsImage,
  },
  props: {
    selectedLensColour: {
      type: String,
      default: 'blue',
    },
    allScenes: {
      type: String,
      required: true,
    },
    selectedScene: {
      type: String,
      required: true,
    },
    customisedProduct: {
      type: CustomisedProduct,
      required: true,
    },
    showIrisPreview: {
      type: Boolean,
      default: false,
    },
    productMode: {
      type: Boolean,
      default: false,
    },
    initialScenes: {
      type: Object,
      default: () => {},
    },
    inCart: {
      type: Boolean,
      default: false,
    },
  },
  async setup (props) {
    if (!props.allScenes) return

    const { isMdBreakpointOrAbove } = useBreakpoint()

    const query = getAllScenes(props.allScenes)
    const { data: result } = await useDatoQuery({ query })

    const allLensGuideScenes = computed(() => result?.value?.allLensGuideScenes)

    return {
      isMdBreakpointOrAbove,
      allLensGuideScenes,
      lensGuideScene: ref(props.initialScenes)
    }
  },
  
  data() {
    return {
      isPressed: false,
      pressCount: 0,
      query: null,
      showOverlay: true,
      label: 'Press & Hold',
      grabbing: false,
      xPercent: 50,
      minValue: 5,
      maxValue: 95,
      nakedeyeLoaded: false,
      primaryLoaded: false,
      secondaryLoaded: false,
      previousSelectedNakedEyeImage: null,
      previousPrimarySceneImage: null,
      previousSecondarySceneImage: null
    }
  },
  computed: {
    selectedNakedEyeImage() {
      return this.lensGuideScene?.nakedEyeImage?.responsiveImage
    },
    resolvedLensColour () {
      // Some more recent purchases of green lenses have used the sku 'grn' for some unknown reason
      if (this.selectedLensColour === 'grn') return 'green'
      return this.selectedLensColour
    },
    selectedSceneImages() {
      return Object.values(this.lensGuideScene?.sceneImages || {}).find(
        (image) =>
          image.lensType === this.customisedProduct.lensTech &&
          image.lensColour === this.selectedLensColour
      )
    },
    primarySceneImage() {
      return this.selectedSceneImages?.image?.responsiveImage
    },
    secondarySceneImage() {
      return this.selectedSceneImages?.secondaryImage?.responsiveImage
    },
    rangeValue() {
      if (!this.isMdBreakpointOrAbove) return 0
      return this.xPercent
    },
    clip() {
      if (!this.isMdBreakpointOrAbove) return

      return {
        'clip-path': `polygon(${this.xPercent}% 0, 100% 0, 100% 100%, ${this.xPercent}% 100%)`,
      }
    },
    showInstruction() {
      return this.pressCount < 2
    },
    hasLensTechFiltering() {
      return this.customisedProduct.allowLensTechFiltering
    },
    hasScenes() {
      return !!Object.keys(this.lensGuideScene).length
    },
    lensTechIcon() {
      if (this.customisedProduct.lensTech === 'iris') return '8ko'
      if (this.customisedProduct.isPrescriptionSunglassesLens) {
        if (this.customisedProduct.lensChoice?.lensTech === 'rx') return null
        return this.customisedProduct.lensChoice?.lensTech
      }
      return this.customisedProduct.lensTech
    },
  },
  watch: {
    selectedScene() {
      this.nakedeyeLoaded = false
      this.primaryLoaded = false
      this.secondaryLoaded = false
      this.lensGuideScene = this.allLensGuideScenes.find(
        (scene) => scene.sceneName.toLowerCase() === this.selectedScene
      )
    },
    primarySceneImage () {
      this.primaryLoaded = false
    },
    secondarySceneImage () {
      this.secondaryLoaded = false
    },
    xPercent(newValue) {
      if (newValue <= this.minValue) {
        this.xPercent = this.minValue
      }

      if (newValue >= this.maxValue) {
        this.xPercent = this.maxValue
      }
    },
    initialScenes() {
      if (Object.keys(!this.lensGuideScene.length))
        this.lensGuideScene = this.initialScenes
    },
  },
  mounted() {
    this.loadAllLensGuideScenes()

    return setTimeout(() => {
      this.showOverlay = false
    }, 3000)
  },
  methods: {
    pressed() {
      if (this.isMdBreakpointOrAbove) return
      this.isPressed = true
      this.showOverlay = false
      this.label = 'Naked Eye'
    },
    released() {
      if (this.isMdBreakpointOrAbove) return
      this.isPressed = false
      this.pressCount++
      if (this.customisedProduct.lensTechSelected) {
        this.label = 'Press & Hold'
      } else {
        this.label = ''
      }
    },
    grab() {
      this.grabbing = true
    },
    release() {
      this.grabbing = false
    },
    loadAllLensGuideScenes() {
      if (!this.allLensGuideScenes) return
      // Preload all lookthroughs on page load
      this.allLensGuideScenes.forEach((scene) => {
        usePreloadImage(
          scene.nakedEyeImage.responsiveImage.srcSet,
          scene.nakedEyeImage.responsiveImage.webpSrcSet,
          scene.nakedEyeImage.responsiveImage.sizes
        )
        scene.sceneImages.forEach((sceneImage) => {
          usePreloadImage(
            sceneImage.image.responsiveImage.srcSet,
            sceneImage.image.responsiveImage.webpSrcSet,
            sceneImage.image.responsiveImage.sizes
          )
          if (sceneImage.secondaryImage) {
            usePreloadImage(
              sceneImage.secondaryImage.responsiveImage.srcSet,
              sceneImage.secondaryImage.responsiveImage.webpSrcSet,
              sceneImage.secondaryImage.responsiveImage.sizes
            )
          }
        })
      })
    },
    handlePrimaryLoaded () {
      this.primaryLoaded = true
      this.previousPrimarySceneImage = this.primarySceneImage
    },
    handleSecondaryLoaded () {
      this.secondaryLoaded = true
      this.previousSecondarySceneImage = this.secondarySceneImage
    },
    handleNakedEyeLoaded () {
      this.nakedeyeLoaded = true
      this.previousSelectedNakedEyeImage = this.selectedNakedEyeImage
    }
  },
}
</script>
<style lang="postcss">
.LensGuideMainImage {
  @apply relative h-0 bg-grey-light;
  padding-bottom: 120%;

  @screen sm {
    padding-bottom: 75%;
  }

  @screen md {
    @apply w-full h-full p-0;
  }

  &--in-cart {
    @screen short {
      padding-bottom: 100%;
    }

    @screen xShort {
      padding-bottom: 80%;
    }

    @screen xxShort {
      padding-bottom: 75%;
    }
  }

  .LensGuideMainImage__grab-icon {
    @apply absolute top-1/2
      transform -translate-x-1/2 -translate-y-1/2
      flex items-center justify-center
      w-14 h-14
      bg-white/50 rounded-full;

    @screen mdMax {
      @apply hidden;
    }
  }

  .LensGuideMainImage__lens-icon {
    @apply absolute bottom-0
      w-full
      flex flex-col items-center
      pointer-events-none;

    height: 3.5rem;
    margin-bottom: 1.5rem;

    @screen md {
      @apply hidden;
    }
  }

  .LensGuideMainImage__label--desktop,
  .LensGuideMainImage__lens-tech-icon-wrapper {
    @apply absolute bottom-0
      p-6;

    @screen lg {
      @apply p-12;
    }

    @screen mdMax {
      @apply hidden;
    }
  }
}

.LensGuideMainImage__base-image,
.LensGuideMainImage__preview-image,
.LensGuideMainImage__preview-image-wrapper,
.LensGuideMainImage__overlay,
.LensGuideMainImage__gradient-overlay {
  @apply absolute inset-0
    w-full h-full
    pointer-events-none;

  @screen mdMax {
    @apply cursor-pointer;
  }
}

.LensGuideMainImage__preview-image-wrapper {
  @apply opacity-100;
  transition: opacity 0.2s ease-in-out;

  &--hidden {
    @apply opacity-0;
  }
}

.LensGuideMainImage__scene-image {
  @apply w-full h-full pointer-events-none;

  img {
    @apply object-cover;
  }

  &--previous {
    @apply !absolute !inset-0;
  }
}

.LensGuideMainImage__overlay {
  @apply bg-black/60;

  @screen md {
    @apply hidden;
  }
}

.LensGuideMainImage__product-image {
  @apply absolute inset-0
    w-full h-full
    bg-grey-light
    pointer-events-none;
}

.LensGuideMainImage__label-wrapper {
  @apply absolute bottom-0
    w-full
    flex flex-col items-center
    mb-11;

  @screen md {
    @apply hidden;
  }
}

.LensGuideMainImage__label {
  @apply text-white;
}

.LensGuideMainImage__lens-tech-icon-wrapper {
  @apply right-0;
}

.LensGuideMainImage__lens-tech-icon {
  @apply w-24 h-8;
}

.LensGuideMainImage__icon-wrapper {
  @apply absolute inset-0
    flex items-center justify-center;

  @screen md {
    @apply hidden;
  }
}

@screen mdMax {
  .LensGuideMainImage__preview-image {
    @apply transition-opacity duration-150;
  }

  .LensGuideMainImage--is-pressed .LensGuideMainImage__preview-image {
    @apply opacity-0;
  }
}

.LensGuideMainImage__toggle-wrapper {
  @apply absolute left-0 bottom-0
    w-14
    ml-7 mb-7;

  @screen md {
    @apply hidden;
  }
}

.LensGuideMainImage__grab-handle {
  @apply absolute top-1/2
    transform -translate-x-1/2 -translate-y-1/2
    z-10;

  @screen mdMax {
    @apply hidden;
  }
}

.LensGuideMainImage__range-input {
  @apply absolute top-1/2
    h-full
    transform -translate-y-1/2
    z-10
    opacity-0
    cursor-grab;

  @screen mdMax {
    @apply hidden;
  }

  &--grabbing {
    @apply cursor-grabbing;
  }
}
</style>
